import React, { useState } from "react";
import ArchiveRecord from "./ArchiveRecord";
import RequestConfirmDialog from "./RequestConfirmDialog";

/**
 * The action set an ARCHIVED request carries on its card — sibling of
 * RequestDraftActions, same shape: presentational, handlers handed in, one
 * component so the card and the list row cannot drift apart.
 *
 * A live request offers "View pitches"; an archived one offers its history
 * and its way back instead:
 *
 *   Record       flips the card open into the record (summary + accepted
 *                pitches, per the record addendum). The record is fetched
 *                the first time it is opened — mounting ArchiveRecord IS the
 *                lazy load — and simply hidden on re-close, so reopening does
 *                not refetch.
 *   Reactivate   behind the handoff's confirm dialog, word for word. The
 *                transition itself is handed in (useClubTenderActions'
 *                reactivateRequest), so the card, the list row and the kebab
 *                fire the identical handler.
 *
 * The Record button carries the request's pitch count ("Record (14 pitches)")
 * like the design's flipped card, read from the row the list already has —
 * the record endpoint is not called just to label a closed button.
 */
const RequestArchiveActions = ({ tender, onReactivate, isSaving = false }) => {
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordMounted, setRecordMounted] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);

  const total = tender?.total_pitches;
  const recordLabel =
    total == null ? "Record" : `Record (${total} pitches)`;

  const toggleRecord = (event) => {
    event.stopPropagation();
    setRecordMounted(true);
    setRecordOpen((open) => !open);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={toggleRecord}
          className="min-h-[44px] flex-1 min-w-[132px] rounded-lg border border-[#E6E6E6] bg-white px-4 text-sm font-medium text-[#1E1E1E] hover:bg-gray-50 inline-flex items-center justify-center gap-2"
        >
          {recordLabel}
          <span
            aria-hidden="true"
            className={`text-[11px] inline-block transition-transform ${
              recordOpen ? "rotate-180" : ""
            }`}
          >
            ▾
          </span>
        </button>
        {onReactivate && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setShowReactivate(true);
            }}
            disabled={isSaving}
            className="min-h-[44px] flex-1 min-w-[132px] rounded-lg border border-[#E6E6E6] bg-white px-4 text-sm font-medium text-[#1E1E1E] hover:bg-gray-50 disabled:opacity-50"
          >
            Reactivate
          </button>
        )}
      </div>
      {recordMounted && (
        <div
          className={
            recordOpen ? "border-t border-[#EDF0F4] pt-3" : "hidden"
          }
        >
          <ArchiveRecord tender={tender} variant="card" />
        </div>
      )}

      <RequestConfirmDialog
        isOpen={showReactivate}
        title="Reactivate this request?"
        body="It goes live again, agents are notified, and fresh timers start."
        confirmLabel="Reactivate"
        loading={isSaving}
        onClose={() => setShowReactivate(false)}
        onConfirm={async () => {
          const ok = await onReactivate?.();
          if (ok !== false) setShowReactivate(false);
        }}
      />
    </div>
  );
};

export default RequestArchiveActions;
